/**
 * Centralized environment configuration for Seller Admin
 *
 * This module provides a global env object that can be configured programmatically
 * instead of relying on import.meta.env. This is essential for consistent configuration
 * across different services (API client, SSE service, etc.).
 */

export type EnvironmentMode = 'development' | 'production' | 'test';

/**
 * Environment configuration interface
 */
export interface SellerAdminEnvConfig {
  // Environment
  NODE_ENV: EnvironmentMode;
  ENV: EnvironmentMode;

  // API Configuration
  API_BASE_URL: string;
  SSE_BASE_URL: string;
  VITE_API_URL?: string;

  // Store Configuration
  STORE_ID: string;
  AUTH_TOKEN?: string;

  // Feature Flags
  DEBUG?: string;
  VITE_DEBUG_AUTH?: string;

  // Third-party Services
  VITE_BUILDER_API_KEY?: string;
  VITE_RAZORPAY_KEY_ID?: string;

  // MinIO Configuration (if needed)
  MINIO_HOST?: string;
  MINIO_PORT?: string;
}

/**
 * Default environment configuration
 */
const DEFAULT_CONFIG: SellerAdminEnvConfig = {
  NODE_ENV: 'production',
  ENV: 'production',
  API_BASE_URL: 'https://server.shiphouse.in',
  SSE_BASE_URL: 'https://server.shiphouse.in',
  STORE_ID: 'default-store',
};

/**
 * Current environment configuration (mutable)
 */
let currentConfig: SellerAdminEnvConfig = { ...DEFAULT_CONFIG };

/**
 * Flag to track if config has been initialized
 */
let isInitialized = false;

/**
 * Set environment configuration programmatically
 * This should be called at the app entry point before any components mount
 *
 * @param config - Partial configuration to override defaults
 *
 * @example
 * ```ts
 * import { setEnv } from './config/env';
 *
 * setEnv({
 *   NODE_ENV: 'development',
 *   API_BASE_URL: 'http://localhost:8000',
 *   SSE_BASE_URL: 'http://localhost:8000',
 *   STORE_ID: 'my-store-id',
 * });
 * ```
 */
export function setEnv(config: Partial<SellerAdminEnvConfig>): void {
  currentConfig = {
    ...currentConfig,
    ...config,
  };
  isInitialized = true;

  if (typeof window !== 'undefined' && currentConfig.DEBUG === 'true') {
    console.log('🔧 [Seller Admin] Environment configured:', currentConfig);
  }
}

/**
 * Auto-detect configuration from import.meta.env (build-time)
 * This runs automatically on import for convenience in Vite apps
 */
function autoDetectFromImportMetaEnv(): void {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    const detected: Partial<SellerAdminEnvConfig> = {};

    // Environment
    if (import.meta.env.NODE_ENV) {
      detected.NODE_ENV = import.meta.env.NODE_ENV as EnvironmentMode;
    }
    if (import.meta.env.MODE) {
      detected.ENV = import.meta.env.MODE as EnvironmentMode;
    }

    // API
    if (import.meta.env.VITE_API_URL) {
      detected.API_BASE_URL = import.meta.env.VITE_API_URL;
      detected.SSE_BASE_URL = import.meta.env.VITE_API_URL;
      detected.VITE_API_URL = import.meta.env.VITE_API_URL;
    }

    // Store
    if (import.meta.env.STORE_ID) {
      detected.STORE_ID = import.meta.env.STORE_ID;
    }
    if (import.meta.env.AUTH_TOKEN) {
      detected.AUTH_TOKEN = import.meta.env.AUTH_TOKEN;
    }

    // Feature flags
    if (import.meta.env.DEBUG) {
      detected.DEBUG = import.meta.env.DEBUG;
    }
    if (import.meta.env.VITE_DEBUG_AUTH) {
      detected.VITE_DEBUG_AUTH = import.meta.env.VITE_DEBUG_AUTH;
    }

    // Third-party
    if (import.meta.env.VITE_BUILDER_API_KEY) {
      detected.VITE_BUILDER_API_KEY = import.meta.env.VITE_BUILDER_API_KEY;
    }
    if (import.meta.env.VITE_RAZORPAY_KEY_ID) {
      detected.VITE_RAZORPAY_KEY_ID = import.meta.env.VITE_RAZORPAY_KEY_ID;
    }

    // MinIO
    if (import.meta.env.MINIO_HOST) {
      detected.MINIO_HOST = import.meta.env.MINIO_HOST;
    }
    if (import.meta.env.MINIO_PORT) {
      detected.MINIO_PORT = import.meta.env.MINIO_PORT;
    }

    if (Object.keys(detected).length > 0) {
      currentConfig = {
        ...currentConfig,
        ...detected,
      };
    }
  }
}

// Auto-detect on module load for Vite convenience
autoDetectFromImportMetaEnv();

/**
 * Get current environment configuration
 * Returns a readonly proxy to prevent accidental mutations
 */
export function getEnv(): Readonly<SellerAdminEnvConfig> {
  return currentConfig;
}

/**
 * Helper: Check if in development mode
 */
export function isDevelopment(): boolean {
  return currentConfig.NODE_ENV === 'development' || currentConfig.ENV === 'development';
}

/**
 * Helper: Check if in production mode
 */
export function isProduction(): boolean {
  return currentConfig.NODE_ENV === 'production' || currentConfig.ENV === 'production';
}

/**
 * Helper: Check if running on localhost
 */
export function isLocalhost(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  );
}

/**
 * Helper: Get effective API URL
 * Automatically detects localhost and returns appropriate URL
 */
export function getApiUrl(): string {
  // If running on localhost, use localhost API
  if (isLocalhost() && isDevelopment()) {
    return 'http://localhost:8000';
  }

  return currentConfig.API_BASE_URL || 'https://server.shiphouse.in';
}

/**
 * Helper: Get effective SSE URL
 * Automatically detects localhost and returns appropriate URL
 */
export function getSseUrl(): string {
  // If running on localhost, use localhost SSE
  if (isLocalhost() && isDevelopment()) {
    return 'http://localhost:8000';
  }

  return currentConfig.SSE_BASE_URL || 'https://server.shiphouse.in';
}

/**
 * Helper: Get effective store ID
 */
export function getStoreId(): string {
  return currentConfig.STORE_ID || 'default-store';
}

/**
 * Helper: Get Builder.io API key
 */
export function getBuilderApiKey(): string | undefined {
  return currentConfig.VITE_BUILDER_API_KEY;
}

/**
 * Helper: Get Razorpay key ID
 */
export function getRazorpayKeyId(): string | undefined {
  return currentConfig.VITE_RAZORPAY_KEY_ID;
}

/**
 * Helper: Check if debug mode is enabled
 */
export function isDebugMode(): boolean {
  return currentConfig.DEBUG === 'true' || currentConfig.VITE_DEBUG_AUTH === 'true';
}

/**
 * Export the env config object directly for convenience
 * Use this for direct property access
 */
export const env = new Proxy(currentConfig, {
  get(target, prop: string) {
    return currentConfig[prop as keyof SellerAdminEnvConfig];
  },
});

// Type export for consuming applications
export type { SellerAdminEnvConfig as EnvConfig };
